import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { 
  CheckCircle2,
  AlertTriangle,
  HelpCircle,
  ListOrdered,
  FileText,
  Download,
  Compass,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronRight,
  Printer
} from 'lucide-react';
import { beslutslageTranslations } from '../../lib/beslutslageTranslations';
import { SALES_ENABLED } from '../../lib/featureFlags';

/**
 * BeslutslagePremium - Full Premium Decision Support Content
 * 
 * Skogskalkyl = von der Zahl zur Entscheidung
 * 
 * Premium modules:
 * 1. Lägesbild - Clear situation assessment
 * 2. Osäkerheter - Prioritized uncertainties
 * 3. Beslutsbehov - Decision readiness assessment
 * 4. Nästa steg - 3-5 prioritized next steps
 * 5. Kort beslutsbrev - Compact decision summary
 */

const BeslutslagePremium = ({ 
  language = 'sv',
  result,
  inputs,
  onDownloadPDF,
  onPrint
}) => {
  // Wartemodus: never render Premium content in public UI while sales are paused.
  if (!SALES_ENABLED) return null;

  const t = beslutslageTranslations[language] || beslutslageTranslations.sv;
  
  // Generate dynamic premium content based on calculation
  const generatePremiumContent = () => {
    const region = inputs?.region || 'svealand';
    const areal = inputs?.areal || 50;
    const volym = inputs?.virkesvolym || 7500;
    const midValue = result?.midEstimate || 0;
    const pricePerHa = midValue / areal;
    
    const regionNames = {
      norrland: 'norra Sverige',
      svealand: 'mellersta Sverige', 
      gotaland: 'södra Sverige'
    };
    
    const benchmarks = {
      norrland: 65000,
      svealand: 85000,
      gotaland: 110000
    };
    const benchmark = benchmarks[region];
    const isAbove = pricePerHa > benchmark * 1.1;
    const isBelow = pricePerHa < benchmark * 0.9;
    
    const volymPerHa = volym / areal;
    const isTimberDriven = volymPerHa > 150;
    
    // 1. LÄGESBILD
    const lagesbild = {
      title: 'Lägesbild',
      summary: isAbove 
        ? `Fastigheten värderas högre än genomsnittet i ${regionNames[region]}. Detta beror främst på ${isTimberDriven ? 'ett högt virkesförråd per hektar' : 'markens attraktiva läge'}.`
        : isBelow
        ? `Värderingen ligger under regionsnitt. ${isTimberDriven ? 'Trots högt virkesförråd påverkar markläget negativt.' : 'Både virkesförråd och markläge bidrar till lägre värdering.'}`
        : `Fastigheten ligger nära regionsnitt för ${regionNames[region]}. Ingen tydlig över- eller undervärdering.`,
      keyPoints: [
        `Uppskattat värde: ${(midValue / 1000000).toFixed(2)} MSEK`,
        `Pris per hektar: ${Math.round(pricePerHa).toLocaleString('sv-SE')} kr`,
        `Volym per hektar: ${Math.round(volymPerHa)} m³sk`,
        isAbove ? 'Position: Över regionsnitt' : isBelow ? 'Position: Under regionsnitt' : 'Position: Nära regionsnitt'
      ]
    };
    
    // 2. OSÄKERHETER (prioriterade)
    const osakerheter = {
      title: 'Osäkerheter',
      intro: 'Följande faktorer kan skifta värdet mest:',
      factors: [
        {
          factor: 'Virkesförråd ej verifierat',
          impact: 'hög',
          direction: 'båda',
          desc: 'Verklig inventering kan ändra värdet ±15-25%'
        },
        {
          factor: 'Bonitet uppskattad',
          impact: 'medel',
          direction: volymPerHa > 120 ? 'upp' : 'ned',
          desc: 'Faktisk bonitet påverkar tillväxt och framtida avkastning'
        },
        {
          factor: 'Marknadsfaktorer',
          impact: 'medel',
          direction: 'båda',
          desc: 'Lokalt köptryck och konjunktur ej inräknat'
        },
        {
          factor: 'Åtkomlighet och vägnät',
          impact: isTimberDriven ? 'medel' : 'låg',
          direction: 'ned',
          desc: 'Dålig åtkomlighet sänker nettovärdet vid avverkning'
        }
      ]
    };
    
    // 3. BESLUTSBEHOV
    const beslutsbehov = {
      title: 'Beslutsbehov',
      question: 'Räcker detta för beslut?',
      assessments: [
        {
          type: 'first_indication',
          label: 'Första prisindikation',
          answer: 'ja',
          desc: 'Underlaget ger en rimlig första uppfattning om värdenivå.'
        },
        {
          type: 'internal_discussion',
          label: 'Intern diskussion',
          answer: 'ja',
          desc: 'Kan användas som utgångspunkt för samtal med delägare.'
        },
        {
          type: 'sale_decision',
          label: 'Slutligt säljbeslut',
          answer: 'nej',
          desc: 'Kräver verifierat virkesförråd och extern värdering.'
        },
        {
          type: 'bank_financing',
          label: 'Bank / finansiering',
          answer: 'nej',
          desc: 'Bank kräver officiell värdering med skogsbruksplan.'
        }
      ],
      recommendation: isAbove 
        ? 'Överväg extern värdering om försäljning är aktuell inom 12 månader.'
        : isBelow
        ? 'Avvakta gärna tills virkesförrådet verifierats innan större beslut.'
        : 'Underlaget räcker för intern planering, men fördjupa vid affärsbeslut.'
    };
    
    // 4. NÄSTA STEG (3-5 prioriserade)
    const nastaSteg = {
      title: 'Nästa steg',
      intro: 'Prioriterade åtgärder baserat på nuvarande läge:',
      steps: [
        {
          priority: 1,
          action: 'Verifiera virkesförråd',
          how: 'Beställ inventering eller uppdatera skogsbruksplan',
          why: 'Viktigaste osäkerhetsfaktorn – påverkar värdet mest'
        },
        {
          priority: 2,
          action: isAbove ? 'Kontakta 2-3 mäklare' : 'Bedöm tidhorisont',
          how: isAbove ? 'Begär indikativa värderingar utan bindning' : 'Bestäm om 1, 3 eller 5+ års perspektiv',
          why: isAbove ? 'Marknadsintresse avgör slutligt pris' : 'Påverkar om åtgärder lönar sig'
        },
        {
          priority: 3,
          action: 'Kontrollera delägarsituation',
          how: 'Granska fastighetsregister och ev. samäganderättsavtal',
          why: 'Oklarheter försenar och komplicerar affär'
        },
        ...(isTimberDriven ? [{
          priority: 4,
          action: 'Utvärdera avverkningspotential',
          how: 'Gå igenom åldersfördelning och gallringsbehov',
          why: 'Högt förråd kan motivera delförsäljning av virke'
        }] : [{
          priority: 4,
          action: 'Undersök alternativ användning',
          how: 'Jakt, friluftsliv, arrende – vad höjer värdet?',
          why: 'Lågt förråd kompenseras ibland av andra värden'
        }])
      ]
    };
    
    // 5. KORT BESLUTSBREV
    const beslutsbrev = {
      title: 'Sammanfattning',
      date: new Date().toLocaleDateString('sv-SE'),
      sections: {
        arende: `Värdering av skogsfastighet, ${areal} ha i ${regionNames[region]}`,
        resultat: `Uppskattat värde: ${(midValue / 1000000).toFixed(2)} MSEK (spann: ${(result?.lowEstimate / 1000000).toFixed(2)}–${(result?.highEstimate / 1000000).toFixed(2)} MSEK)`,
        position: isAbove ? 'Över regionsnitt' : isBelow ? 'Under regionsnitt' : 'Nära regionsnitt',
        huvudosakerheter: 'Virkesförråd ej verifierat, bonitet uppskattad, marknadsfaktorer ej inräknade',
        beslutslage: beslutsbehov.recommendation,
        prioriteradAtgard: nastaSteg.steps[0].action
      }
    };
    
    return { lagesbild, osakerheter, beslutsbehov, nastaSteg, beslutsbrev };
  };

  const content = generatePremiumContent();

  const ImpactBadge = ({ impact, direction }) => {
    const colors = {
      hög: 'bg-red-100 text-red-700 border-red-200',
      medel: 'bg-amber-100 text-amber-700 border-amber-200',
      låg: 'bg-green-100 text-green-700 border-green-200'
    };
    const DirectionIcon = direction === 'upp' ? TrendingUp : direction === 'ned' ? TrendingDown : Minus;
    
    return (
      <div className="flex items-center gap-2">
        <span className={`px-2 py-0.5 text-xs font-medium rounded border ${colors[impact]}`}>
          {impact}
        </span>
        <DirectionIcon className="h-3 w-3 text-gray-400" />
      </div>
    );
  };

  const AnswerBadge = ({ answer }) => {
    const config = {
      ja: { icon: CheckCircle2, color: 'text-green-600', bg: 'bg-green-50' },
      nej: { icon: AlertTriangle, color: 'text-red-600', bg: 'bg-red-50' },
      delvis: { icon: HelpCircle, color: 'text-amber-600', bg: 'bg-amber-50' }
    };
    const { icon: Icon, color, bg } = config[answer] || config.delvis;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${color} ${bg}`}>
        <Icon className="h-3 w-3" />
        {answer.charAt(0).toUpperCase() + answer.slice(1)}
      </span>
    );
  };

  return (
    <div className="space-y-6" data-testid="beslutslage-premium-content">
      {/* Premium Header */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-forest-600 to-forest-700 rounded-lg text-white">
        <div className="flex items-center gap-3">
          <CheckCircle2 className="h-6 w-6" />
          <div>
            <h2 className="text-lg font-semibold">Beslutsläge aktiverat</h2>
            <p className="text-forest-100 text-sm">Fördjupat beslutsunderlag för detta ärende</p>
          </div>
        </div>
        <span className="px-3 py-1 bg-white/20 rounded-full text-sm">
          Premium
        </span>
      </div>

      {/* 1. LÄGESBILD */}
      <Card className="border-forest-200" data-testid="premium-lagesbild">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-forest-800">
            <Compass className="h-5 w-5" />
            1. {content.lagesbild.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-700 leading-relaxed">
            {content.lagesbild.summary}
          </p>
          <div className="grid grid-cols-2 gap-3">
            {content.lagesbild.keyPoints.map((point, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-gray-600 bg-forest-50 p-2 rounded">
                <ChevronRight className="h-4 w-4 text-forest-500" />
                {point}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* 2. OSÄKERHETER */}
      <Card className="border-amber-200" data-testid="premium-osakerheter">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-amber-800">
            <AlertTriangle className="h-5 w-5" />
            2. {content.osakerheter.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">{content.osakerheter.intro}</p>
          <div className="space-y-3">
            {content.osakerheter.factors.map((item, i) => (
              <div key={i} className="p-3 bg-amber-50/50 rounded-lg border border-amber-100">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-gray-800">{item.factor}</span>
                  <ImpactBadge impact={item.impact} direction={item.direction} />
                </div>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* 3. BESLUTSBEHOV */}
      <Card className="border-blue-200" data-testid="premium-beslutsbehov">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-blue-800">
            <HelpCircle className="h-5 w-5" />
            3. {content.beslutsbehov.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="font-medium text-gray-800">{content.beslutsbehov.question}</p>
          <div className="space-y-2">
            {content.beslutsbehov.assessments.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <AnswerBadge answer={item.answer} />
                <div className="flex-1">
                  <p className="font-medium text-gray-800 text-sm">{item.label}</p>
                  <p className="text-xs text-gray-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="text-sm text-blue-700 italic border-l-2 border-blue-300 pl-3 py-1 bg-blue-50/50 rounded-r">
            {content.beslutsbehov.recommendation}
          </p>
        </CardContent>
      </Card>

      {/* 4. NÄSTA STEG */}
      <Card className="border-purple-200" data-testid="premium-nasta-steg">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-purple-800">
            <ListOrdered className="h-5 w-5" />
            4. {content.nastaSteg.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600">{content.nastaSteg.intro}</p>
          <div className="space-y-3">
            {content.nastaSteg.steps.map((step, i) => (
              <div key={i} className="flex gap-3 p-3 bg-purple-50/50 rounded-lg border border-purple-100">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-purple-600 text-white text-sm flex items-center justify-center font-semibold">
                  {step.priority}
                </span>
                <div className="flex-1">
                  <p className="font-medium text-purple-800">{step.action}</p>
                  <p className="text-sm text-gray-600 mt-0.5">{step.how}</p>
                  <p className="text-xs text-gray-500 mt-1 italic">{step.why}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* 5. KORT BESLUTSBREV */}
      <Card className="border-2 border-forest-300 bg-gradient-to-br from-forest-50 to-white" data-testid="premium-beslutsbrev">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-forest-800">
            <FileText className="h-5 w-5" />
            5. {content.beslutsbrev.title}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Decision Letter Content */}
          <div className="p-4 bg-white rounded-lg border border-forest-200 space-y-3 print:border-none" id="beslutsbrev-content">
            <div className="flex justify-between items-start border-b border-gray-100 pb-2">
              <div>
                <p className="font-semibold text-forest-800">Skogskalkyl Beslutsbrev</p>
                <p className="text-xs text-gray-500">Genererat {content.beslutsbrev.date}</p>
              </div>
              <span className="text-xs px-2 py-0.5 bg-forest-100 text-forest-700 rounded">
                Beslutsläge Premium
              </span>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-[120px_1fr] gap-2">
                <span className="text-gray-500 font-medium">Ärende:</span>
                <span className="text-gray-800">{content.beslutsbrev.sections.arende}</span>
              </div>
              <div className="grid grid-cols-[120px_1fr] gap-2">
                <span className="text-gray-500 font-medium">Resultat:</span>
                <span className="text-gray-800">{content.beslutsbrev.sections.resultat}</span>
              </div>
              <div className="grid grid-cols-[120px_1fr] gap-2">
                <span className="text-gray-500 font-medium">Position:</span>
                <span className="text-gray-800">{content.beslutsbrev.sections.position}</span>
              </div>
              <div className="grid grid-cols-[120px_1fr] gap-2">
                <span className="text-gray-500 font-medium">Osäkerheter:</span>
                <span className="text-gray-800">{content.beslutsbrev.sections.huvudosakerheter}</span>
              </div>
              <div className="grid grid-cols-[120px_1fr] gap-2">
                <span className="text-gray-500 font-medium">Bedömning:</span>
                <span className="text-gray-800">{content.beslutsbrev.sections.beslutslage}</span>
              </div>
              <div className="grid grid-cols-[120px_1fr] gap-2">
                <span className="text-gray-500 font-medium">Nästa steg:</span>
                <span className="text-gray-800 font-medium">{content.beslutsbrev.sections.prioriteradAtgard}</span>
              </div>
            </div>
            
            <div className="pt-2 border-t border-gray-100 text-xs text-gray-400">
              Skogskalkyl.se · Från siffra till bättre beslut
            </div>
          </div>

          {/* Export Buttons */}
          <div className="flex gap-2 pt-2">
            <Button 
              onClick={onDownloadPDF}
              className="flex-1 bg-forest-700 hover:bg-forest-800 text-white"
              data-testid="download-pdf-btn"
            >
              <Download className="mr-2 h-4 w-4" />
              Ladda ner PDF
            </Button>
            <Button 
              onClick={onPrint}
              variant="outline"
              className="border-forest-300 text-forest-700 hover:bg-forest-50"
              data-testid="print-btn"
            >
              <Printer className="mr-2 h-4 w-4" />
              Skriv ut
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BeslutslagePremium;
